<template>
  <GridChartCard
    :title="title"
    ref="chartCard"
    @draw="createChart"
    :hasData="chartData.labels.length > 0"
    :noDataText="viewingPaused ? $t('noLoadsCurrentlyPaused') : undefined"
  >
    <template #actions>
      <Icon
      v-if="isInteractive && !isByproduct"
      iconColor="white"
      :small="false"
      :icon="myTractsFilterIcon"
      :tooltipText="myTractsFilterTooltip"
      margin="mr-3"
      @icon-clicked="filterMyTracts = !filterMyTracts ; refresh()"/>
      <Icon
      v-if="isInteractive"
      iconColor="white"
      :icon="reservationFilterIcon"
      :tooltipText="reservationFilterTooltip"
      :small="false"
      margin="mr-3"
      @icon-clicked="filterReservation = !filterReservation ; refresh()"/>
      <Icon
      v-if="isInteractive && productIcon"
      iconColor="white"
      :tooltipText="productGroupingTooltip"
      :small="false"
      margin="mr-3"
      :disabled="data.data.length === 0"
      @icon-clicked="groupByProduct = !groupByProduct"
      :icon="productIcon"/>
      <Icon
      iconColor="white"
      :tooltipText="viewingPaused ? $t('allLoads') : $t('pausedLoads')"
      :small="false"
      margin="mr-3"
      :disabled="data.data.length === 0 || noPausedLoads"
      @icon-clicked="viewingPaused = !viewingPaused"
      :icon="viewingPaused ? 'mdi-pound' : 'mdi-pause-circle-outline'"/>
      <Icon
      iconColor="white"
      :tooltipText="$t('refresh')"
      margin="mr-3"
      @icon-clicked="refresh"
      icon="mdi-refresh"
      :small="false"/>
    </template>
  </GridChartCard>
</template>

<script>
import { Chart } from 'chart.js'
import { mapActions, mapGetters } from 'vuex'
import { getChartColor } from './ChartColors'
import IconHelpers from '@/utils/IconHelpers'
import { ContractMode } from '@/utils/Enumerations.js'
import { chartTooltipStyle } from '@/utils/ChartHelpers'

export default {
  name: 'StackedBarChartCardForTicketsForPickup',

  components: {
    GridChartCard: () => import('./GridChartCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    IconHelpers,
    chart: null,
    viewingPaused: false,
    groupByProduct: true,
    filterReservation: false,
    filterMyTracts: false
  }),

  props: {
    data: {
      type: Object,
      required: true
    },
    elementId: {
      type: String,
      required: true
    },
    isByproduct: {
      type: Boolean,
      default: false
    },
    isInteractive: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    data: {
      handler () {
        this.scheduleDraw()
      },
      deep: true
    },

    viewingPaused () {
      this.scheduleDraw()
    },

    groupByProduct () {
      this.scheduleDraw()
    }
  },

  computed: {
    ...mapGetters('dashboard', ['interactiveChartTooltipFooter']),

    title () {
      return (this.viewingPaused) ? this.$t('pausedLoads') : this.$t('loadsReadyForPickup')
    },

    headerStyle () {
      return this.isByproduct ? 'background-color: #363636; color: white;' : 'background-color: #d15f27; color: white;'
    },

    getToolTipText () {
      return this.viewingPaused ? this.countUnitToggleToolTip : this.weightUnitToggleToolTip
    },

    weightUnitToggleToolTip () {
      return (this.type === 'ItemizedDefectSummaryChart') ? 'Percentage by Tons' : 'Tons'
    },

    countUnitToggleToolTip () {
      return (this.type === 'ItemizedDefectSummaryChart') ? 'Percentage by Quantity' : 'Load Count'
    },

    noPausedLoads () {
      return this.data.data.every(d => d.paused === 0)
    },

    contractColorSwitch () {
      return this.isByproduct ? ContractMode.Byproducts.value : ContractMode.Logs.value
    },

    chartData () {
      const { dataSets } = this.data
      const missing = new Set(dataSets.labels.map((_, i) => dataSets.sets.every(s => (this.viewingPaused) ? s.data[i].pausedLoads === 0 : s.data[i].readyLoads === 0) ? i : -1))
      const notMissingIndex = (_, i) => !missing.has(i)

      const data = this.groupByProduct
        ? {
          labels: dataSets.labels.filter(notMissingIndex),
          datasets: dataSets.sets.map((set, index) => {
            return {
              label: set.label,
              data: set.data.filter(notMissingIndex).map(d => (this.viewingPaused) ? d.pausedLoads : d.readyLoads),
              ticketIds: set.ticketIds.filter(notMissingIndex).map(d => (this.viewingPaused) ? d.paused : d.ready),
              backgroundColor: getChartColor(index, dataSets.sets.length, this.contractColorSwitch)
            }
          })
        }
        : {
          labels: dataSets.labels.filter(notMissingIndex),
          datasets: dataSets.labels.filter(notMissingIndex).reduce((a, _, index) => {
            a[0].data.push(dataSets.sets.map(s => s.data.filter(notMissingIndex)[index][this.viewingPaused ? 'pausedLoads' : 'readyLoads']).reduce((a, c) => a + c, 0))
            a[0].backgroundColor.push(getChartColor(index, dataSets.labels.filter(notMissingIndex).length, this.contractColorSwitch))
            const tn = []
            dataSets.sets.map(s => s.ticketIds.filter(notMissingIndex)[index][this.viewingPaused ? 'paused' : 'ready']).forEach(t => { t.forEach(t => tn.push(t)) })
            a[0].ticketIds.push(tn)
            return a
          }, [{ data: [], backgroundColor: [], ticketIds: [] }])
        }
      return data
    },

    productIcon () {
      return IconHelpers.getIconForObjectType('product', this.groupByProduct)
    },

    reservationFilterIcon () {
      return IconHelpers.getIconForObjectType('reservation', this.filterReservation)
    },

    myTractsFilterIcon () {
      return IconHelpers.getIconForObjectType('myTracts', this.filterMyTracts)
    },

    productGroupingTooltip () {
      return this.$t(this.groupByProduct
        ? 'disableGroupingByCategory'
        : 'groupByCategory',
      {
        category: this.$t('product')
      })
    },

    reservationFilterTooltip () {
      return this.filterReservation ? this.$t('showAllLoads') : this.$t('showOnlyUnreservedLoads')
    },

    myTractsFilterTooltip () {
      return this.filterMyTracts ? this.$t('showAllTracts') : this.$t('showOnlyMyTracts')
    }
  },

  methods: {
    ...mapActions('dashboard', ['getNestedCSV', 'getWidgetInteractionStatus']),

    scheduleDraw () {
      this.$nextTick(() => {
        if (this.$refs.chartCard) this.$refs.chartCard.draw()
      })
    },

    updateChart () {
      const { dataSets } = this.data
      this.chart.data.datasets.forEach((dataset, index) => {
        dataset.data = (this.viewingPaused) ? dataSets[index].data.pausedLoads : dataSets[index].data.readyLoads
      })
    },

    createChart (canvas) {
      const chart = new Chart(canvas, {
        type: 'bar',
        data: this.chartData,
        options: {
          maintainAspectRatio: false,
          responsive: true,
          resizeDelay: 500,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              ...chartTooltipStyle
            }
          },
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              ticks: {
                stepSize: 1
              }
            }
          },
          onClick: (e) => this.onDataClicked(e)
        }
      })
      if (this.isInteractive) chart.options.plugins.tooltip.callbacks.footer = () => this.interactiveChartTooltipFooter
      this.chart = chart
    },

    async onDataClicked (e) {
      if (this.isInteractive) {
        const element = this.chart.getElementsAtEventForMode(e, 'point', true)
        const data = this.chart.config._config.data
        const xAxisValue = data.labels[element[0]?.index]
        const yAxisValue = data.datasets[element[0]?.datasetIndex]?.label
        if (await this.getWidgetInteractionStatus({ widgetId: this.widgetId, x: xAxisValue, y: yAxisValue })) this.$emit('data-clicked', { xAxisValue: xAxisValue, yAxisValue: yAxisValue, paused: this.viewingPaused })
      }
    },

    refresh () {
      this.$emit('refresh', { limitToUnreserved: this.filterReservation, myTracts: this.filterMyTracts })
    }
  }
}
</script>
